import React, { Fragment, ReactElement } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSortAsc, faSortDesc } from "@fortawesome/free-solid-svg-icons";
import { ColumnInfo, SortingColumnInfo } from "../ColumnInfo";
import { SortRule } from "../../sorting";
import ToolTip from "../../../components/common/ToolTip/ToolTip";
import './GenericSortingTableHeader.css';

interface GenericSortingTableHeaderProps<T> {
    columns: SortingColumnInfo[],
    sortRules: SortRule<T>[],
    setSortRules: React.Dispatch<React.SetStateAction<SortRule<T>[]>>,
}

export default function GenericSortingTableHeader<T>(props: GenericSortingTableHeaderProps<T>): ReactElement {

    const getColumnDisplay = (column: ColumnInfo) => {
        const placeHolderDisplay = <>&nbsp;</>
        let display = column.display ? column.display : placeHolderDisplay
        if (column.extraDisplay) {
            display = <div className="flex flex-row gap-025em">{display}{column.extraDisplay}</div>
        }
        if (column.tooltip) {
            return <ToolTip contents={display}>{column.tooltip}</ToolTip>
        } else {
            return display
        }
    }

    const getSortingIndicator = (column: ColumnInfo) => {
        const field = column.sortingId ? column.sortingId : column.id
        let sortDir = "nosort"
        const index = props.sortRules.findIndex((rule) => rule.key === field)
        if (index >= 0) {
            const rule = props.sortRules[index]
            sortDir = rule.dir
        }

        switch (sortDir) {
            case "asc":
                return (
                    <div className="sorting-indicator">
                        <div className="sorting-arrow">
                            <FontAwesomeIcon icon={faSortAsc} />
                        </div>
                        <div className="sorting-order">
                            {(index + 1)}
                        </div>
                    </div>
                )
            case "desc":
                return (
                    <div className="sorting-indicator">
                        <div className="sorting-arrow">
                            <FontAwesomeIcon icon={faSortDesc} />
                        </div>
                        <div className="sorting-order">
                            {(index + 1)}
                        </div>
                    </div>
                )
            default: // nosort
                return (<>
                    {
                        column.noSortingDisplay ?
                            <div className="sorting-indicator">
                                <div className="sorting-arrow">
                                    {column.noSortingDisplay}
                                </div>
                                <div className="sorting-order">
                                </div>
                            </div>
                            :
                            <></>
                    }
                </>
                )
        }
    }

    const handleSort = (column: ColumnInfo) => {
        const field = column.sortingId ? column.sortingId : column.id
        const type = column.type ? column.type : "string"
        const sortRule = props.sortRules.find((rule) => rule.key === field)
        if (!sortRule) {
            const newSortRule: SortRule<T> = {
                key: field,
                type: type,
                dir: "asc",
                dataLkpFunc: column.dataLkpFunc,
                comparator: column.comparator
            } as SortRule<T>
            props.setSortRules([...props.sortRules, newSortRule])
        } else if (sortRule.dir === "asc") {
            sortRule.dir = "desc"
            props.setSortRules([...props.sortRules])
        } else if (sortRule.dir === "desc") {
            props.setSortRules([...props.sortRules.filter((rule) => rule.key !== field)])
        }
    }

    return (
        <thead>
            <tr>
                {
                    props.columns.map((column, index) => {
                        if (column.headerActions && column.headerActions.length > 0) {
                            return (
                                <th key={"header-action-" + index}>
                                    <div className="flex flex-row align-items-center justify-content-center gap-025em">
                                        {
                                            column.headerActions.map((action, idx) => {
                                                return <Fragment key={idx}>{action}</Fragment>
                                            })
                                        }
                                    </div>
                                </th>
                            )
                        } else {
                            if (column.sortingDisabled) {
                                return (
                                    <th key={"header-sortingDisabled-" + index}
                                        className={column.headerClassName}>
                                        <div>{getColumnDisplay(column)}</div>
                                    </th>
                                )
                            } else {
                                return (
                                    <th key={"header-" + (column.sortingId ? column.sortingId : column.id)}
                                        className={column.headerClassName}
                                        onClick={() => { handleSort(column) }}>
                                        <div className="sorting-column-header">
                                            <div>{getColumnDisplay(column)}</div>
                                            {getSortingIndicator(column)}
                                        </div>
                                    </th>
                                )
                            }
                        }
                    }
                    )
                }
            </tr>
        </thead>
    )
}
